import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Container,
  createFilterOptions,
  FormControlLabel,
  styled,
  TextField as MUITextField,
  Typography,
} from '@mui/material';
import { PayloadAction } from '@reduxjs/toolkit';
import arrayMutators from 'final-form-arrays';
import { makeValidate, TextField } from 'mui-rff';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import { useClassicNavigate } from '../../../hooks/useClassicNavigate';
import {
  getAccountsAndOpportunities,
  getFolder,
  getFolders,
  postFolder,
  updateFolder,
} from '../../../redux/actions/folderActions';
import { selectAppStatus, setCurrentApp } from '../../../redux/reducers/appStatusReducer';
import { setCurrentFolder } from '../../../redux/reducers/folderReducer';
import { AppDispatch } from '../../../redux/store';
import { checkProjectFolderExists, editProject, saveProject } from '../../../services/dealGPT';
import type { CreateProjectOptions, Project, UpdateProjectOptions } from '../../../Types/dealGPT';
import { AppEnum, IndexType, ProjectStatus } from '../../../Types/enums';
import { AccountType, Folder, OpportunityType, UpdateFolder } from '../../../Types/folder';
import LearnMore from '../../LearnMore';
import LoadingMask from '../../LoadingMask';
import { useAuth } from '../../Providers/AuthProvider';
import { baseSchema, intellioSchema } from './formSchemas';
import IntellioAdvantageForm from './IntellioAdvantageForm';

const FolderContainer = styled('div')(() => ({
  width: '100%',
  position: 'relative',
  zIndex: 1,
  background: 'white',
  height: '100%',
  overflowY: 'auto',
}));

const StyledForm = styled('form')({
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  margin: '0 20px 20px 20px',
});

const StyledButton = styled(Button)({
  width: '120px',
  height: '35px',
  marginLeft: '20px',
});

const initialFolderValues = {
  id: '',
  name: '',
  description: '',
  systemPrompt: '',
  contributors: [],
  accountId: '',
  accountName: '',
  opportunityId: '',
  opportunityName: '',
  folderType: '',
  project: {
    id: '',
    clientName: '',
    projectType: 'Diligence',
    name: '',
    description: '',
    status: ProjectStatus.OPEN,
    teamsId: '',
    teamsLink: '',
    opportunityId: '',
    channelId: '',
    indexToUse: IndexType.OPENAI,
    sharepointFolderURL: '',
    ignoredFiles: [] as string[],
    ignoredFolders: [] as string[],
  } as Project,
};

const EditFolder = ({ folder }: { folder?: Folder }): JSX.Element => {
  const navigate = useClassicNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isAdmin, isDealGPTAdmin } = useAuth();
  const { selectedApp } = useSelector(selectAppStatus);
  const [currentFormFolder, setCurrentFormFolder] = useState<UpdateFolder>(initialFolderValues);
  const [isLoading, setIsLoading] = useState(false);
  const [isIntellioAdvantageProject, setIsIntellioAdvantageProject] = useState<boolean>(
    folder?.id ? !!folder?.projectId : selectedApp === AppEnum.DEAL_GPT
  );
  const [userAccounts, setUserAccounts] = useState<AccountType[]>([{ id: '', name: '' }]);
  const [userOpportunities, setUserOpportunities] = useState<OpportunityType[]>([]);

  const validate = useMemo(() => {
    let schema = baseSchema;

    if (isIntellioAdvantageProject) {
      schema = baseSchema.concat(intellioSchema);
    }

    return makeValidate(schema);
  }, [isIntellioAdvantageProject]);

  useEffect(() => {
    // Fetch user accounts and opportunities
    dispatch(getAccountsAndOpportunities()).then(
      (
        action: PayloadAction<
          { accounts: AccountType[]; opportunities: OpportunityType[] } | unknown
        >
      ) => {
        const { accounts, opportunities } = action.payload as {
          accounts: AccountType[];
          opportunities: OpportunityType[];
        };
        setUserAccounts([{ id: '', name: '' }, ...accounts]);
        setUserOpportunities(opportunities);
      }
    );

    if (!folder) {
      dispatch(setCurrentFolder());
    } else {
      if (folder.project) {
        setCurrentFormFolder(folder);
      } else {
        setCurrentFormFolder({
          ...folder,
          project: initialFolderValues.project,
        });
      }

      setIsIntellioAdvantageProject(
        folder?.id ? !!folder?.projectId : selectedApp === AppEnum.DEAL_GPT
      );
    }
  }, [folder, dispatch, selectedApp]);

  const onSubmit = useCallback(
    async (values: UpdateFolder) => {
      setIsLoading(true);

      let response: Project | undefined = undefined;
      if (isIntellioAdvantageProject) {
        const formProject = values['project'] as Project;
        const project = {
          name: values['name'],
          description: values['description'] ?? undefined,
          clientName: formProject['clientName'],
          projectType: formProject['projectType'],
          opportunityId: formProject['opportunityId'],
          teamsLink: formProject['teamsLink'] ?? undefined,
          teamsId: formProject['teamsId'],
          channelId: formProject['channelId'],
          indexToUse: formProject['indexToUse'],
          sharepointFolderURL: formProject['sharepointFolderURL'] ?? undefined,
          ignoredFolders: formProject['ignoredFolders'] ?? [],
          ignoredFiles: formProject['ignoredFiles'] ?? [],
          activeStartDate: formProject['activeStartDate'],
          activeEndDate: formProject['activeEndDate'],
        } as UpdateProjectOptions;

        try {
          if (folder?.project?.id) {
            // update the project
            project.id = folder?.projectId as string;
            project.status = formProject['status'];
            response = await editProject(project);
          } else {
            response = await saveProject(project as CreateProjectOptions);
          }
        } catch {
          setIsLoading(false);
          enqueueSnackbar('Failed to save project.', {
            variant: 'error',
            autoHideDuration: 5000,
          });
          return;
        }

        try {
          const folderExistsResponse = await checkProjectFolderExists(
            project.teamsId,
            project.channelId,
            project.sharepointFolderURL
          );

          // The way we fill data on the backend passes a false in here if there is no sharepointURL
          if (!folderExistsResponse) {
            enqueueSnackbar('Folder not found, check the root folder URL', { variant: 'error' });
          }
        } catch {
          enqueueSnackbar('Failed to check folder existence', { variant: 'error' });
          setIsLoading(false);
          return;
        }

        if (!response.id) {
          setIsLoading(false);
          enqueueSnackbar('Failed to save project!', {
            variant: 'error',
            autoHideDuration: 5000,
          });
          return;
        }
      }

      dispatch(setCurrentApp(isIntellioAdvantageProject ? AppEnum.DEAL_GPT : AppEnum.NIGEL));

      if (folder?.id) {
        // update the folder
        const updatedFolder = {
          name: values['name'],
          description: values['description'] ?? '',
          systemPrompt: values['systemPrompt'],
          accountId: values['accountId'] ?? '',
          accountName: values['accountName'] ?? '',
          opportunityId: values['opportunityId'] ?? '',
          opportunityName: values['opportunityName'] ?? '',
          folderType: values['opportunityId']
            ? 'OPPORTUNITY'
            : values['accountId']
            ? 'ACCOUNT'
            : 'INTERNAL',
          projectId: response?.id,
        } as UpdateFolder;
        await dispatch(
          updateFolder({
            folderId: folder.id,
            updatedFields: {
              ...updatedFolder,
              folderType: values['opportunityId']
                ? 'OPPORTUNITY'
                : values['accountId']
                ? 'ACCOUNT'
                : 'INTERNAL',
            },
          })
        );
        await dispatch(getFolders({ foldersWithProjects: folder.projectId ? true : false }));
        await dispatch(getFolder({ folderId: folder.id, isAdmin }));
        setIsLoading(false);
        navigate(`/folder/${folder.id}`);
      } else {
        // create a folder
        const action = await dispatch(
          postFolder({
            name: values['name'],
            description: values['description'] ?? '',
            systemPrompt: values['systemPrompt'],
            projectId: response?.id,
            contributors: [],
            accountId: values['accountId'],
            accountName: values['accountName'],
            opportunityId: values['opportunityId'],
            opportunityName: values['opportunityName'],
            folderType: values['opportunityId']
              ? 'OPPORTUNITY'
              : values['accountId']
              ? 'ACCOUNT'
              : 'INTERNAL',
          })
        );

        const retFolder = action.payload as Folder;
        await dispatch(getFolders({ foldersWithProjects: !!response?.id }));
        await dispatch(getFolder({ folderId: retFolder.id, isAdmin }));
        setIsLoading(false);
        navigate(`/folder/${retFolder.id}`);
      }
    },
    [folder, navigate, isIntellioAdvantageProject, dispatch, isAdmin]
  );

  return (
    <FolderContainer>
      <div style={{ height: 'fit-content', position: 'relative' }}>
        <LoadingMask isLoading={isLoading} />
        <Container maxWidth="md">
          <Form
            validate={validate}
            onSubmit={onSubmit}
            initialValues={currentFormFolder}
            mutators={{ ...arrayMutators }}
            render={({ handleSubmit, form, values }) => (
              <StyledForm onSubmit={handleSubmit}>
                <Typography
                  variant="h4"
                  style={{
                    fontWeight: '700',
                    fontSize: '20px',
                    marginTop: '20px',
                    marginBottom: '5px',
                  }}
                >
                  {folder?.id ? 'Edit Folder' : 'Create Folder'}
                </Typography>

                {isDealGPTAdmin && (
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      aria-controls="intellio-advantage-content"
                      id="intellio-advantage-header"
                    >
                      <Typography>Advanced Configurations</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isIntellioAdvantageProject}
                            disabled={folder && !!folder.projectId}
                            onChange={(e) => setIsIntellioAdvantageProject(e.target.checked)}
                            name="isIntellioAdvantageProject"
                            color="primary"
                          />
                        }
                        label="Intellio® Advantage Project"
                      />
                    </AccordionDetails>
                  </Accordion>
                )}

                {userOpportunities.length > 0 && (
                  <div>
                    <Typography variant="body2">Opportunites</Typography>
                    <div
                      style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '5px' }}
                    >
                      {userOpportunities.map((opportunity) => (
                        <Chip
                          variant={values.opportunityId === opportunity.id ? 'filled' : 'outlined'}
                          sx={{
                            color: '#070154',
                            fontSize: '13px',
                            fontWeight: '500',
                            height: '24px',
                          }}
                          key={opportunity.id}
                          label={opportunity.name}
                          onClick={() => {
                            if (values.opportunityId === opportunity.id) {
                              form.change('opportunityName', '');
                              form.change('opportunityId', '');
                              form.change('accountId', '');
                              form.change('accountName', '');
                              form.change('name', '');
                            } else {
                              form.change('opportunityName', opportunity.name);
                              form.change('opportunityId', opportunity.id);
                              form.change('accountId', opportunity.accountId);
                              form.change(
                                'accountName',
                                opportunity.accountName ?? 'Account ID: ' + opportunity.accountId
                              );
                              form.change('name', opportunity.name);
                            }
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {userAccounts.length > 1 && (
                  <Autocomplete
                    options={userAccounts}
                    getOptionLabel={(account) =>
                      (account as AccountType).name ?? 'Account ID: ' + account.id
                    }
                    filterOptions={createFilterOptions({
                      matchFrom: 'any',
                      limit: 10,
                    })}
                    renderInput={(params) => (
                      <MUITextField
                        {...params}
                        label="Account"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        sx={{
                          '& .MuiAutocomplete-clearIndicator': {
                            transform: 'translateY(50%)',
                          },
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={{
                      name: values.accountName ?? 'Account ID: ' + values.accountId,
                      id: values.accountId,
                    }}
                    onChange={(_, newValue) => {
                      if (newValue) {
                        form.change('accountId', newValue.id);
                        form.change('accountName', newValue.name ?? 'Account ID: ' + newValue.id);
                        form.change('name', newValue.name ?? 'Account ID: ' + newValue.id);
                      } else {
                        form.change('accountId', '');
                        form.change('accountName', '');
                        form.change('name', '');
                      }
                    }}
                    sx={{
                      paddingTop: '16px',
                      '& .MuiAutocomplete-inputRoot': {
                        paddingRight: '0 !important',
                      },
                      '& .MuiAutocomplete-popupIndicator': {
                        right: '10px',
                        transform: 'translateY(50%) translateX(40%)',
                      },
                    }}
                  />
                )}

                <TextField
                  label="Folder Name"
                  name="name"
                  required
                  InputLabelProps={{ shrink: true }}
                  margin="normal"
                />
                <TextField
                  label="Description"
                  name="description"
                  multiline
                  minRows={2}
                  InputLabelProps={{ shrink: true }}
                  margin="normal"
                />
                {!isIntellioAdvantageProject && (
                  <>
                    <TextField
                      label="Folder Knowledge"
                      name="systemPrompt"
                      placeholder="What should this folder always remember?"
                      InputLabelProps={{ shrink: true }}
                      margin="normal"
                      multiline
                      minRows={2}
                    />
                    <LearnMore
                      link="https://howto.westmonroe.com/a/1972439"
                      sx={{ mr: '5px', ml: '10px' }}
                    />
                  </>
                )}

                {isIntellioAdvantageProject && (
                  <IntellioAdvantageForm
                    form={form}
                    setIsLoading={setIsLoading}
                    currentProject={folder?.project ?? initialFolderValues.project}
                  />
                )}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <StyledButton
                    id="cancel-button"
                    variant="outlined"
                    onClick={() => {
                      if (folder?.id) {
                        navigate(`/folder/${folder.id}`);
                      } else {
                        if (selectedApp === AppEnum.DEAL_GPT) {
                          navigate('/intellio-advantage');
                        } else {
                          navigate('/folder/chat');
                        }
                      }
                    }}
                  >
                    Cancel
                  </StyledButton>
                  <StyledButton id="save-button" type="submit" variant="contained">
                    Save
                  </StyledButton>
                </div>
              </StyledForm>
            )}
          />
        </Container>
      </div>
    </FolderContainer>
  );
};

export default EditFolder;
